import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Users,
  Wallet,
  RefreshCw,
  FileSpreadsheet,
  TrendingUp,
  Landmark,
  Scale,
  Download,
  FileText,
  GitCompare,
  Building2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  DsButton,
  DsCard,
  DsIcon,
  DsInput,
  DsLabel,
  DsPageHeader,
  DsBadge,
} from "../../../design-system";
import { payrollOpsService, salaryStructureService } from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";
import ApplyAutoStructuresModal from "./ApplyAutoStructuresModal";
import { exportPayrollDashboardExcel } from "./exportPayrollDashboardExcel";
import {
  exportPayrollCardPdf,
  exportPayrollDashboardPdf,
} from "./exportPayrollDashboardPdf";
import { exportPayrollCardExcel } from "./exportPayrollCardExcel";
import {
  AnalyticsChartPanel,
  CHART_HEX,
  DashCombo,
  DashDonut,
  DashHBar,
  DashLine,
} from "./payrollDashboardCharts";
import {
  DashboardAlertsPanel,
  DashboardAttendanceTab,
  DashboardDeductionsTab,
  DashboardFnfTab,
  DashboardLiabilityTab,
  DashboardMovementsTab,
} from "./PayrollDashboardExtendedTabs";
import {
  buildCardDetailPayload,
  computeReadinessMetrics,
  dmyToYm,
  formatInrFull,
  formatInrLakhs,
  resolvePulseDisplay,
  statusBadgeLabel,
} from "./payrollDashboardAnalytics";

function MiniSpark({ values = [], color = CHART_HEX[0] }) {
  const nums = (values || []).map((v) => Number(v) || 0);
  if (nums.length < 2) return null;
  const max = Math.max(...nums, 1);
  const w = 72;
  const h = 22;
  const step = w / (nums.length - 1);
  const points = nums
    .map((v, i) => `${i * step},${h - (v / max) * (h - 2) - 1}`)
    .join(" ");
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden>
      <polyline fill="none" stroke={color} strokeWidth="1.75" points={points} />
    </svg>
  );
}

function MetricCard({
  label,
  value,
  sub,
  icon: Icon,
  tone = "default",
  spark = null,
  bars = null,
  donut = null,
  onClick = null,
  clickHint = true,
}) {
  const valueTone =
    tone === "up"
      ? "text-[hsl(var(--ds-success))]"
      : tone === "down"
        ? "text-[hsl(var(--ds-danger))]"
        : "text-[hsl(var(--ds-fg))]";

  const inner = (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))] text-[10px] leading-tight">
          {label}
        </div>
        <div className={`mt-0.5 text-base font-bold leading-tight tabular-nums ${valueTone}`}>
          {value}
        </div>
        {sub ? (
          <div className="ds-caption mt-0.5 text-[10px] leading-snug text-[hsl(var(--ds-muted-fg))]">
            {sub}
          </div>
        ) : null}
        {spark ? <div className="mt-1">{spark}</div> : null}
        {bars || null}
        {onClick && clickHint ? (
          <div className="mt-1 text-[9px] text-[hsl(var(--ds-primary))]">Click for details</div>
        ) : null}
      </div>
      <div className="flex flex-col items-end gap-1">
        {donut ||
          (Icon ? (
            <DsIcon
              icon={Icon}
              size="sm"
              className="text-[hsl(var(--ds-secondary))] opacity-70"
            />
          ) : null)}
      </div>
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full rounded-[var(--ds-radius-md)] text-left outline-none transition hover:ring-2 hover:ring-[hsl(var(--ds-ring))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
      >
        <DsCard padding="sm" className="!p-2.5 h-full">
          {inner}
        </DsCard>
      </button>
    );
  }

  return (
    <DsCard padding="sm" className="!p-2.5">
      {inner}
    </DsCard>
  );
}

function LoadingOverlay({ show }) {
  if (!show) return null;
  return (
    <div
      className="absolute inset-0 z-40 flex items-center justify-center"
      style={{ background: "hsl(var(--ds-primary) / 0.12)" }}
      aria-busy="true"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface)/0.92)] px-8 py-6 shadow-lg backdrop-blur-sm">
        <div className="relative h-14 w-14">
          <svg className="h-14 w-14 -rotate-90" viewBox="0 0 56 56" aria-hidden>
            <circle
              cx="28"
              cy="28"
              r="22"
              fill="none"
              stroke="hsl(var(--ds-muted))"
              strokeWidth="5"
            />
            <circle
              cx="28"
              cy="28"
              r="22"
              fill="none"
              stroke="hsl(var(--ds-primary))"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="100 40"
              className="animate-spin origin-center"
              style={{ transformOrigin: "28px 28px" }}
            />
          </svg>
        </div>
        <div className="text-sm font-semibold text-[hsl(var(--ds-fg))]">Loading analytics…</div>
        <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
          Fetching register &amp; period selection
        </div>
      </div>
    </div>
  );
}

const COMPARE_ASPECTS = [
  { id: "headcount", label: "Headcount" },
  { id: "fixedCost", label: "Fixed cost" },
  { id: "totalCost", label: "Payroll cost" },
  { id: "cash", label: "Cash outflow" },
  { id: "net", label: "Bank / Net" },
  { id: "tds", label: "TDS" },
  { id: "taxRegime", label: "Tax regime mix" },
  { id: "statutory", label: "Statutory" },
  { id: "masters", label: "Master-wise" },
  { id: "fnf", label: "F&F / exits" },
  { id: "revisions", label: "Salary revisions" },
  { id: "attendance", label: "Attendance vs payroll" },
];

function CompareFormModal({
  open,
  onClose,
  availableMonths = [],
  selected = [],
  selectedAspects = [],
  onApply,
}) {
  const [picked, setPicked] = useState(selected);
  const [aspects, setAspects] = useState(selectedAspects);

  useEffect(() => {
    if (open) {
      setPicked(selected);
      setAspects(
        selectedAspects?.length
          ? selectedAspects
          : COMPARE_ASPECTS.map((a) => a.id)
      );
    }
  }, [open, selected, selectedAspects]);

  if (!open) return null;

  const toggle = (ym) => {
    setPicked((prev) => {
      if (prev.includes(ym)) return prev.filter((x) => x !== ym);
      if (prev.length >= 5) {
        toast.error("Select up to 5 periods");
        return prev;
      }
      return [...prev, ym].sort();
    });
  };

  const toggleAspect = (id) => {
    setAspects((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-lg">
        <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <div className="ds-h4">Compare periods &amp; aspects</div>
            <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              Choose 2–5 pay months and which dashboard aspects to compare.
            </div>
          </div>
          <DsButton type="button" variant="ghost" size="sm" onClick={onClose}>
            <DsIcon icon={X} size="sm" />
          </DsButton>
        </div>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-4 py-3">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase text-[hsl(var(--ds-muted-fg))]">
              Pay months
            </div>
            {(availableMonths || []).length === 0 ? (
              <div className="ds-body-sm text-[hsl(var(--ds-muted-fg))]">
                No pay months with runs yet. Process / finalise payroll to compare.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {availableMonths.map((m) => {
                  const ym = m.payMonth || m;
                  const on = picked.includes(ym);
                  return (
                    <button
                      key={ym}
                      type="button"
                      onClick={() => toggle(ym)}
                      className={`rounded-md border px-2 py-1.5 text-left text-xs ${
                        on
                          ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.12)]"
                          : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted)/0.4)]"
                      }`}
                    >
                      <div className="font-medium">{ym}</div>
                      {m.finalizedAt ? (
                        <div className="text-[10px] text-[hsl(var(--ds-success))]">Finalised</div>
                      ) : (
                        <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
                          {m.status || "Run"}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase text-[hsl(var(--ds-muted-fg))]">
                Aspects (UI)
              </div>
              <button
                type="button"
                className="text-[10px] text-[hsl(var(--ds-primary))] underline"
                onClick={() => setAspects(COMPARE_ASPECTS.map((a) => a.id))}
              >
                Select all
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {COMPARE_ASPECTS.map((a) => {
                const on = aspects.includes(a.id);
                return (
                  <label
                    key={a.id}
                    className={`flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-xs ${
                      on
                        ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)]"
                        : "border-[hsl(var(--ds-border))]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleAspect(a.id)}
                      className="rounded border-[hsl(var(--ds-border))]"
                    />
                    {a.label}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
          <DsButton type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </DsButton>
          <DsButton
            type="button"
            size="sm"
            disabled={picked.length < 2 || aspects.length < 1}
            onClick={() => {
              onApply({ months: picked, aspects });
              onClose();
            }}
          >
            Apply comparison
          </DsButton>
        </div>
      </div>
    </div>
  );
}

function CardDetailModal({ open, detail, onClose, canExport }) {
  const [exporting, setExporting] = useState(false);
  if (!open || !detail) return null;

  const runExcel = async () => {
    if (!canExport) {
      toast.error("No export permission");
      return;
    }
    setExporting(true);
    try {
      const name = await exportPayrollCardExcel(detail);
      toast.success(`Exported ${name}`);
    } catch (e) {
      toast.error(e.message || "Excel export failed");
    } finally {
      setExporting(false);
    }
  };

  const runPdf = async () => {
    if (!canExport) {
      toast.error("No export permission");
      return;
    }
    setExporting(true);
    try {
      const name = exportPayrollCardPdf(detail);
      toast.success(`Exported ${name}`);
    } catch (e) {
      toast.error(e.message || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-lg">
        <div className="flex items-start justify-between gap-2 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <div className="ds-h4">{detail.title}</div>
            <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              {detail.periodLabel} · {detail.status}
            </div>
          </div>
          <DsButton type="button" variant="ghost" size="sm" onClick={onClose}>
            <DsIcon icon={X} size="sm" />
          </DsButton>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <table className="ds-table w-full text-xs">
            <tbody>
              {(detail.summary || []).map(([k, v]) => (
                <tr key={k} className="border-b border-[hsl(var(--ds-border))]">
                  <td className="py-1.5 pr-3 text-[hsl(var(--ds-muted-fg))]">{k}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">
                    {typeof v === "number" ? formatInrLakhs(v) : v == null ? "—" : String(v)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {detail.note ? (
            <p className="mt-3 text-[10px] text-[hsl(var(--ds-muted-fg))]">{detail.note}</p>
          ) : null}
          {detail.trendRows?.length ? (
            <div className="mt-4">
              <div className="mb-1 text-[10px] font-semibold uppercase text-[hsl(var(--ds-muted-fg))]">
                Trend
              </div>
              <div className="overflow-x-auto">
                <table className="ds-table w-full text-[10px]">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left">Month</th>
                      <th className="px-2 py-1 text-right">Cost</th>
                      <th className="px-2 py-1 text-right">Cash</th>
                      <th className="px-2 py-1 text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.trendRows.map((r) => (
                      <tr key={r.payMonth} className="border-t border-[hsl(var(--ds-border))]">
                        <td className="px-2 py-1">{r.payMonth}</td>
                        <td className="px-2 py-1 text-right">{formatInrLakhs(r.totalCost)}</td>
                        <td className="px-2 py-1 text-right">
                          {formatInrLakhs(r.totalCashFlow)}
                        </td>
                        <td className="px-2 py-1 text-right">{formatInrLakhs(r.netPay)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
          {canExport ? (
            <>
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                disabled={exporting}
                onClick={runExcel}
              >
                <DsIcon icon={Download} size="sm" />
                Excel
              </DsButton>
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                disabled={exporting}
                onClick={runPdf}
              >
                <DsIcon icon={FileText} size="sm" />
                PDF
              </DsButton>
            </>
          ) : null}
          <DsButton type="button" size="sm" onClick={onClose}>
            Close
          </DsButton>
        </div>
      </div>
    </div>
  );
}

/**
 * Payroll Dashboard — corporate analytics (cost, cash, tax, masters, compare).
 * Processed figures: analytics API (register summary). Readiness: existing props.
 */
export default function PayrollDashboardPanel({
  darkMode = false,
  employees = [],
  breakdownByEmployee = null,
  structures = [],
  insightItems = [],
  selectedMonth = 0,
  selectedYear = new Date().getFullYear(),
  oldRegimeCount = 0,
  newRegimeCount = 0,
  onPeriodChange,
  onRefresh,
  loading = false,
  canExport = true,
  showFlipButton = false,
  isFlipped = true,
  onFlip,
}) {
  const now = new Date();
  const [periodMode, setPeriodMode] = useState("month");
  const [month, setMonth] = useState(selectedMonth + 1);
  const [year, setYear] = useState(selectedYear);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [groupBy, setGroupBy] = useState("department");
  const [compareMonths, setCompareMonths] = useState([]);
  const [compareAspects, setCompareAspects] = useState(() =>
    COMPARE_ASPECTS.map((a) => a.id)
  );
  const [compareOpen, setCompareOpen] = useState(false);
  const [cardDetail, setCardDetail] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dashTab, setDashTab] = useState("overview");
  const { hasScreenAccess } = usePermissions();
  const canApplyAutoStructures = hasScreenAccess("payroll", "apply_auto_structures", "create");
  const [savedStructureIds, setSavedStructureIds] = useState(null);
  const [structureMapTick, setStructureMapTick] = useState(0);
  const [applyStructuresOpen, setApplyStructuresOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    salaryStructureService
      .listActiveEmployeeStructureMap()
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setSavedStructureIds(new Set(rows.map((r) => Number(r.employeeId))));
      })
      .catch(() => {
        if (!cancelled) setSavedStructureIds(null);
      });
    return () => {
      cancelled = true;
    };
  }, [employees, structureMapTick]);

  const employeesWithoutSavedStructure = useMemo(
    () =>
      savedStructureIds
        ? (employees || []).filter((e) => e?.id && !savedStructureIds.has(Number(e.id)))
        : [],
    [employees, savedStructureIds]
  );

  useEffect(() => {
    setMonth(selectedMonth + 1);
    setYear(selectedYear);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (periodMode !== "range") return;
    if (rangeFrom && rangeTo) return;
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    if (!rangeFrom) setRangeFrom(`01-${m}-${y}`);
    if (!rangeTo) setRangeTo(`${lastDay}-${m}-${y}`);
  }, [periodMode, rangeFrom, rangeTo, now]);

  useEffect(() => {
    if (typeof onPeriodChange !== "function") return;
    if (periodMode === "month") {
      onPeriodChange({ periodMode, month, year });
    }
  }, [periodMode, month, year, onPeriodChange]);

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    return Array.from({ length: 8 }, (_, i) => current - i);
  }, [now]);

  const readiness = useMemo(
    () =>
      computeReadinessMetrics({
        employees,
        breakdownByEmployee,
        structures,
        insightItems,
      }),
    [employees, breakdownByEmployee, structures, insightItems]
  );

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const params = {
        periodMode,
        year,
        month,
        groupBy,
        comparePayMonths: compareMonths,
      };
      if (periodMode === "range") {
        params.rangeFrom = dmyToYm(rangeFrom);
        params.rangeTo = dmyToYm(rangeTo);
      }
      const res = await payrollOpsService.getDashboardAnalytics(params);
      if (res?.success === false) {
        toast.error(res.message || "Failed to load payroll analytics");
        setAnalytics(null);
      } else {
        setAnalytics(res?.data || res);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Analytics failed");
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [periodMode, year, month, rangeFrom, rangeTo, groupBy, compareMonths]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const trend = useMemo(
    () =>
      analytics?.trendSeries?.length
        ? analytics.trendSeries
        : analytics?.series || [],
    [analytics]
  );
  const seriesCost = useMemo(() => trend.map((s) => s.totalCost || 0), [trend]);
  const seriesCash = useMemo(() => trend.map((s) => s.totalCashFlow || 0), [trend]);
  const seriesHc = useMemo(() => trend.map((s) => s.headcount || 0), [trend]);
  const seriesFixed = useMemo(() => trend.map((s) => s.fixedCost || 0), [trend]);
  const seriesLabels = useMemo(() => trend.map((s) => s.payMonth), [trend]);

  const compareSeries = useMemo(() => {
    if (analytics?.compareSummary?.length) {
      return analytics.compareSummary.map((c) => ({
        label: c.payMonth,
        headcount: c.headcount || 0,
        fixedCost: c.fixedCost || 0,
        totalCost: c.totalCost || 0,
        cash: c.totalCashFlow || 0,
        net: c.netPay || 0,
        tds: c.tds || 0,
        status: c.status,
        taxOld: c.taxOld,
        taxNew: c.taxNew,
        fnfCount: c.fnfCount,
        revisionsCount: c.revisionsCount,
        attOpen: c.attOpen,
      }));
    }
    if (analytics?.compare?.length) {
      return analytics.compare.map((c) => ({
        label: c.label || c.payMonth,
        headcount: c.headcount || 0,
        fixedCost: c.totals?.fixedCost || 0,
        totalCost: c.totals?.totalCost || 0,
        cash: c.totals?.totalCashFlow || 0,
        net: c.totals?.netPay || 0,
        tds: c.totals?.tds || 0,
        status: c.status,
      }));
    }
    return trend.map((s) => ({
      label: s.payMonth,
      headcount: s.headcount || 0,
      fixedCost: s.fixedCost || 0,
      totalCost: s.totalCost || 0,
      cash: s.totalCashFlow || 0,
      net: s.netPay || 0,
      tds: s.tds || 0,
      status: s.status,
    }));
  }, [analytics, trend]);

  const pulse = useMemo(
    () => resolvePulseDisplay(analytics, readiness),
    [analytics, readiness]
  );

  const openCard = useCallback(
    (cardKey) => {
      setCardDetail(
        buildCardDetailPayload(cardKey, {
          analytics,
          readiness,
          pulse,
          trend: analytics?.trendSeries?.length
            ? analytics.trendSeries
            : analytics?.series || [],
        })
      );
    },
    [analytics, readiness, pulse]
  );

  const tax = analytics?.taxRegime || {};
  const oldC = tax.oldCount || oldRegimeCount || 0;
  const newC = tax.newCount || newRegimeCount || 0;
  const masterChartData = useMemo(
    () =>
      (analytics?.masters || []).slice(0, 8).map((m) => ({
        label: m.label,
        value: m.earnedCost || m.fixedCost || 0,
      })),
    [analytics]
  );

  /** Pivot master × compare months for side-by-side department comparison. */
  const masterComparePivot = useMemo(() => {
    const months = (analytics?.compare || [])
      .map((c) => c.payMonth)
      .filter(Boolean);
    if (months.length < 2) return null;
    const map = new Map();
    (analytics.compare || []).forEach((c) => {
      (c.masters || []).forEach((m) => {
        const key = m.label || "—";
        const row = map.get(key) || { label: key, byMonth: {} };
        row.byMonth[c.payMonth] = {
          headcount: m.headcount || 0,
          earnedCost: m.earnedCost || 0,
          fixedCost: m.fixedCost || 0,
          cashOutflow: m.cashOutflow || 0,
          tds: m.tds || 0,
        };
        map.set(key, row);
      });
    });
    const rows = [...map.values()].sort((a, b) => {
      const sumA = months.reduce((s, ym) => s + (a.byMonth[ym]?.earnedCost || 0), 0);
      const sumB = months.reduce((s, ym) => s + (b.byMonth[ym]?.earnedCost || 0), 0);
      return sumB - sumA;
    });
    return { months, rows: rows.slice(0, 25) };
  }, [analytics]);

  const fieldClass =
    "h-8 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 text-xs text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]";

  const showDashboardBody = isFlipped || showFlipButton === false;
  const busy = loading || analyticsLoading;

  const handleExportExcel = async () => {
    if (!canExport) {
      toast.error("You do not have export permission");
      return;
    }
    setExporting(true);
    try {
      const name = await exportPayrollDashboardExcel({
        analytics,
        readiness,
        regimeFallback: { oldCount: oldRegimeCount, newCount: newRegimeCount },
        periodLabel: analytics?.periodLabel,
        compareAspects,
      });
      toast.success(`Exported ${name}`);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Excel export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!canExport) {
      toast.error("You do not have export permission");
      return;
    }
    setExporting(true);
    try {
      const name = exportPayrollDashboardPdf({
        analytics,
        readiness,
        pulse,
        regimeFallback: { oldCount: oldRegimeCount, newCount: newRegimeCount },
        periodLabel: analytics?.periodLabel,
        compareAspects,
      });
      toast.success(`Exported ${name}`);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const availableForCompare = useMemo(() => {
    const fromFinal = analytics?.availableFinalisedMonths || [];
    const fromRuns = analytics?.availableRuns || [];
    const map = new Map();
    [...fromFinal, ...fromRuns].forEach((m) => {
      const ym = m.payMonth || m;
      if (!ym) return;
      if (!map.has(ym)) map.set(ym, m);
    });
    return [...map.values()].sort((a, b) =>
      String(b.payMonth || b).localeCompare(String(a.payMonth || a))
    );
  }, [analytics]);

  const mergedAlerts = useMemo(() => {
    const base = [...(analytics?.alerts || [])];
    const ids = new Set(base.map((a) => a.id));
    const push = (id, tone, title, detail, action = null) => {
      if (ids.has(id)) return;
      ids.add(id);
      base.unshift({ id, tone, title, detail, ...(action ? { action } : {}) });
    };
    if (employeesWithoutSavedStructure.length > 0) {
      push(
        "structure_not_saved",
        "danger",
        "Salary structure not saved",
        `${employeesWithoutSavedStructure.length} employee(s) have no saved structure — Payroll Process will block them.`,
        canApplyAutoStructures
          ? { label: "Apply Auto Structures", onClick: () => setApplyStructuresOpen(true) }
          : null
      );
    }
    if (readiness.missingStructure > 0) {
      push(
        "missing_structure",
        "danger",
        "Missing salary structure",
        `${readiness.missingStructure} employee(s) need structure assignment.`
      );
    }
    if (readiness.missingCtc > 0) {
      push(
        "missing_ctc",
        "danger",
        "Missing CTC",
        `${readiness.missingCtc} employee(s) have no CTC on record.`
      );
    }
    if (readiness.insightStats.pending > 0) {
      push(
        "insights",
        "info",
        "Action insights pending",
        `${readiness.insightStats.pending} insight(s) waiting in the queue.`
      );
    }
    if (readiness.notReady > 0) {
      push(
        "not_ready",
        "warning",
        "Not ready for run",
        `${readiness.notReady} employee(s) blocked (need structure + CTC).`
      );
    }
    return base;
  }, [analytics, readiness, employeesWithoutSavedStructure, canApplyAutoStructures]);

  const TAB_ITEMS = [
    { id: "overview", label: "Overview" },
    { id: "alerts", label: `Alerts${mergedAlerts.length ? ` (${mergedAlerts.length})` : ""}` },
    { id: "movements", label: "Movements" },
    { id: "attendance", label: "Attendance" },
    { id: "deductions", label: "Loans & encash" },
    { id: "fnf", label: "F&F / exits" },
    { id: "liability", label: "Liability" },
  ];

  return (
    <>
      {applyStructuresOpen ? (
        <ApplyAutoStructuresModal
          employees={employeesWithoutSavedStructure}
          onClose={(applied) => {
            setApplyStructuresOpen(false);
            if (!applied) return;
            setStructureMapTick((t) => t + 1);
            if (typeof onRefresh === "function") onRefresh();
          }}
        />
      ) : null}
      {showFlipButton && (
        <DsButton type="button" variant="outline" size="sm" onClick={onFlip}>
          <DsIcon icon={RefreshCw} size="sm" />
          {isFlipped ? "Back" : "Flip Dashboard"}
        </DsButton>
      )}

      {showDashboardBody && (
        <div
          className={`relative flex min-h-0 flex-1 flex-col overflow-y-auto bg-[hsl(var(--ds-surface))] ${
            showFlipButton ? "rounded-xl border border-[hsl(var(--ds-border))]" : "h-full"
          }`}
        >
          <LoadingOverlay show={busy} />
          <div className="ds-page-panel-head">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <DsPageHeader
                title="Payroll Dashboard"
                description={`Pay period: ${analytics?.periodLabel || "—"} · ${statusBadgeLabel(
                  analytics?.status
                )}${busy ? " · Updating…" : ""}`}
              />
              <div className="flex flex-wrap items-center gap-2">
                <DsBadge tone="neutral">{statusBadgeLabel(analytics?.status)}</DsBadge>
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCompareOpen(true)}
                >
                  <DsIcon icon={GitCompare} size="sm" />
                  Compare
                </DsButton>
                {canExport ? (
                  <>
                    <DsButton
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={exporting || busy}
                      onClick={handleExportExcel}
                    >
                      <DsIcon icon={Download} size="sm" />
                      Excel
                    </DsButton>
                    <DsButton
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={exporting || busy}
                      onClick={handleExportPdf}
                    >
                      <DsIcon icon={FileText} size="sm" />
                      PDF
                    </DsButton>
                  </>
                ) : null}
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    loadAnalytics();
                    if (typeof onRefresh === "function") onRefresh();
                  }}
                  disabled={busy}
                >
                  <DsIcon
                    icon={RefreshCw}
                    size="sm"
                    className={busy ? "animate-spin" : ""}
                  />
                  Refresh
                </DsButton>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 lg:px-6">
            <div className="mb-4 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] p-3">
              <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                Period &amp; grouping
              </div>
              <div className="flex flex-wrap items-end gap-2">
                {[
                  { id: "month", label: "Month" },
                  { id: "year", label: "Year" },
                  { id: "range", label: "Period" },
                ].map((tab) => (
                  <DsButton
                    key={tab.id}
                    type="button"
                    size="sm"
                    variant={periodMode === tab.id ? "secondary" : "ghost"}
                    onClick={() => setPeriodMode(tab.id)}
                  >
                    {tab.label}
                  </DsButton>
                ))}

                {periodMode === "month" && (
                  <>
                    <select
                      className={fieldClass}
                      value={month}
                      onChange={(e) => setMonth(Number(e.target.value))}
                      aria-label="Month"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(2000, i, 1).toLocaleString("default", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>
                    <select
                      className={fieldClass}
                      value={year}
                      onChange={(e) => setYear(Number(e.target.value))}
                      aria-label="Year"
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {periodMode === "year" && (
                  <select
                    className={fieldClass}
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    aria-label="Year"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                )}

                {periodMode === "range" && (
                  <>
                    <div>
                      <DsLabel htmlFor="payroll-dash-range-from">From (DD-MM-YYYY)</DsLabel>
                      <DsInput
                        id="payroll-dash-range-from"
                        type="text"
                        className="h-8 max-w-[9rem] text-xs"
                        value={rangeFrom}
                        onChange={(e) => setRangeFrom(e.target.value)}
                        placeholder="01-01-2026"
                      />
                    </div>
                    <div>
                      <DsLabel htmlFor="payroll-dash-range-to">To (DD-MM-YYYY)</DsLabel>
                      <DsInput
                        id="payroll-dash-range-to"
                        type="text"
                        className="h-8 max-w-[9rem] text-xs"
                        value={rangeTo}
                        onChange={(e) => setRangeTo(e.target.value)}
                        placeholder="31-03-2026"
                      />
                    </div>
                  </>
                )}

                <div>
                  <DsLabel htmlFor="payroll-dash-groupby">Master</DsLabel>
                  <select
                    id="payroll-dash-groupby"
                    className={fieldClass}
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                  >
                    <option value="department">Department</option>
                    <option value="branch">Branch</option>
                    <option value="designation">Designation</option>
                    <option value="structure">Salary structure</option>
                  </select>
                </div>
              </div>
              {compareMonths.length > 0 ? (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-[hsl(var(--ds-primary)/0.35)] bg-[hsl(var(--ds-primary)/0.08)] px-2 py-1.5 text-[11px]">
                  <span className="font-semibold text-[hsl(var(--ds-fg))]">
                    {analytics?.compareActive
                      ? "Dashboard showing selected compare months:"
                      : "Comparing:"}{" "}
                    {compareMonths.join(", ")}
                  </span>
                  {analytics?.compareActive ? (
                    <span className="text-[hsl(var(--ds-muted-fg))]">
                      Pulse, charts, tabs &amp; exports use these months (filter paused).
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="text-[hsl(var(--ds-primary))] underline"
                    onClick={() => setCompareMonths([])}
                  >
                    Clear
                  </button>
                </div>
              ) : null}
            </div>

            <div className="mb-4 flex flex-wrap gap-1 border-b border-[hsl(var(--ds-border))] pb-2">
              {TAB_ITEMS.map((tab) => (
                <DsButton
                  key={tab.id}
                  type="button"
                  size="sm"
                  variant={dashTab === tab.id ? "secondary" : "ghost"}
                  onClick={() => setDashTab(tab.id)}
                >
                  {tab.label}
                </DsButton>
              ))}
            </div>

            {dashTab === "alerts" ? (
              <DashboardAlertsPanel alerts={mergedAlerts} />
            ) : dashTab === "movements" ? (
              <DashboardMovementsTab analytics={analytics} />
            ) : dashTab === "attendance" ? (
              <DashboardAttendanceTab analytics={analytics} />
            ) : dashTab === "deductions" ? (
              <DashboardDeductionsTab analytics={analytics} />
            ) : dashTab === "fnf" ? (
              <DashboardFnfTab analytics={analytics} />
            ) : dashTab === "liability" ? (
              <DashboardLiabilityTab analytics={analytics} />
            ) : (
              <>
                {mergedAlerts.length > 0 ? (
                  <div className="mb-3">
                    <DashboardAlertsPanel alerts={mergedAlerts.slice(0, 4)} />
                    {mergedAlerts.length > 4 ? (
                      <button
                        type="button"
                        className="mt-1 text-[10px] text-[hsl(var(--ds-primary))] underline"
                        onClick={() => setDashTab("alerts")}
                      >
                        View all {mergedAlerts.length} alerts
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {(analytics?.status === "no_run" ||
                  analytics?.status === "not_processed") && (
                  <div className="mb-3 rounded-lg border border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.12)] px-3 py-2 text-xs text-[hsl(var(--ds-fg))]">
                    Processed payroll figures are not available for this period yet.
                    Showing structure / readiness where possible. Process payroll to
                    unlock cost, cash outflow, and earned averages.
                  </div>
                )}

                <div className="mb-2 ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  Company pulse
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
                  <MetricCard
                    label="Payroll cost"
                    value={
                      pulse.totalCost == null ? "—" : formatInrLakhs(pulse.totalCost)
                    }
                    sub={
                      pulse.totalCost == null
                        ? "Process payroll for earned cost"
                        : formatInrFull(pulse.totalCost)
                    }
                    icon={Landmark}
                    spark={<MiniSpark values={seriesCost} color={CHART_HEX[0]} />}
                    onClick={() => openCard("payroll_cost")}
                  />
                  <MetricCard
                    label="Cash outflow"
                    value={
                      pulse.totalCashFlow == null
                        ? "—"
                        : formatInrLakhs(pulse.totalCashFlow)
                    }
                    sub={
                      pulse.totalCashFlow == null
                        ? "From register when processed"
                        : formatInrFull(pulse.totalCashFlow)
                    }
                    icon={Wallet}
                    spark={<MiniSpark values={seriesCash} color={CHART_HEX[2]} />}
                    onClick={() => openCard("cash_outflow")}
                  />
                  <MetricCard
                    label="Bank / net"
                    value={
                      pulse.netPay == null ? "—" : formatInrLakhs(pulse.netPay)
                    }
                    sub={
                      pulse.grossPay == null
                        ? "—"
                        : `Gross ${formatInrLakhs(pulse.grossPay)}`
                    }
                    icon={Wallet}
                    spark={
                      <MiniSpark
                        values={trend.map((s) => s.netPay || 0)}
                        color={CHART_HEX[1]}
                      />
                    }
                    onClick={() => openCard("bank_net")}
                  />
                  <MetricCard
                    label="Headcount"
                    value={pulse.headcount}
                    sub={
                      pulse.headcountSource === "register"
                        ? "Payroll register"
                        : "Current payroll scope"
                    }
                    icon={Users}
                    spark={<MiniSpark values={seriesHc} color={CHART_HEX[5]} />}
                    onClick={() => openCard("headcount")}
                  />
                  <MetricCard
                    label="Avg fixed / emp"
                    value={formatInrLakhs(pulse.avgFixed)}
                    sub={`${pulse.fixedSource === "register" ? "Register" : "Structure"} · ${formatInrLakhs(pulse.fixedCost)} · avg = Σ fixed / Σ HC`}
                    icon={FileSpreadsheet}
                    tone="up"
                    spark={<MiniSpark values={seriesFixed} color={CHART_HEX[3]} />}
                    onClick={() => openCard("avg_fixed")}
                  />
                  <MetricCard
                    label="Avg earned / emp"
                    value={
                      pulse.avgEarnedCost == null
                        ? "—"
                        : formatInrLakhs(pulse.avgEarnedCost)
                    }
                    sub="avg = Σ cost / Σ HC (emp-months)"
                    icon={TrendingUp}
                    onClick={() => openCard("avg_earned")}
                  />
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <DsCard padding="sm" className="!p-3">
                    <AnalyticsChartPanel
                      title="Cost vs cash (last months)"
                      labels={seriesLabels}
                      values={seriesCost}
                      lineValues={seriesCash}
                      defaultMode="combo"
                    />
                  </DsCard>
                  <button
                    type="button"
                    className="text-left outline-none transition hover:ring-2 hover:ring-[hsl(var(--ds-ring))] rounded-[var(--ds-radius-md)]"
                    onClick={() => openCard("variance")}
                  >
                    <DsCard padding="sm" className="!p-3 h-full">
                      <div className="mb-2 ds-small uppercase text-[hsl(var(--ds-muted-fg))]">
                        Cash outflow variance (pie) · click for details
                      </div>
                      <DashDonut data={analytics?.variancePie || []} size={128} />
                    </DsCard>
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
                  <button
                    type="button"
                    className="text-left outline-none transition hover:ring-2 hover:ring-[hsl(var(--ds-ring))] rounded-[var(--ds-radius-md)]"
                    onClick={() => openCard("fixed_vs_earned")}
                  >
                    <DsCard padding="sm" className="!p-3 h-full">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="ds-small uppercase text-[hsl(var(--ds-muted-fg))]">
                        Fixed vs earned
                      </div>
                      <DsIcon icon={Scale} size="sm" className="opacity-60" />
                    </div>
                    <div className="mb-2 grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
                          Fixed ({pulse.fixedSource})
                        </div>
                        <div className="text-sm font-bold tabular-nums">
                          {formatInrLakhs(pulse.fixedCost)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">Earned</div>
                        <div className="text-sm font-bold tabular-nums">
                          {pulse.totalCost == null
                            ? "—"
                            : formatInrLakhs(pulse.totalCost)}
                        </div>
                      </div>
                    </div>
                    <DashCombo
                      labels={seriesLabels}
                      barValues={seriesFixed}
                      lineValues={seriesCost}
                      barLabel="Fixed"
                      lineLabel="Earned cost"
                    />
                    </DsCard>
                  </button>

                  <button
                    type="button"
                    className="text-left outline-none transition hover:ring-2 hover:ring-[hsl(var(--ds-ring))] rounded-[var(--ds-radius-md)]"
                    onClick={() => openCard("tax_regime")}
                  >
                    <DsCard padding="sm" className="!p-3 h-full">
                    <div className="mb-2 ds-small uppercase text-[hsl(var(--ds-muted-fg))]">
                      Tax regimes
                    </div>
                    <DashDonut
                      data={[
                        { label: "Old regime", value: oldC },
                        { label: "New regime", value: newC },
                      ]}
                      size={110}
                    />
                    <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                      <div>
                        TDS Old · {formatInrLakhs(tax.tdsOld || 0)}
                      </div>
                      <div>
                        TDS New · {formatInrLakhs(tax.tdsNew || 0)}
                      </div>
                    </div>
                    </DsCard>
                  </button>

                  <button
                    type="button"
                    className="text-left outline-none transition hover:ring-2 hover:ring-[hsl(var(--ds-ring))] rounded-[var(--ds-radius-md)]"
                    onClick={() => openCard("statutory")}
                  >
                    <DsCard padding="sm" className="!p-3 h-full">
                    <div className="mb-2 ds-small uppercase text-[hsl(var(--ds-muted-fg))]">
                      Statutory cash
                    </div>
                    <DashDonut
                      data={[
                        { label: "PF Er", value: pulse.totals.pfEmployer || 0 },
                        { label: "ESI Er", value: pulse.totals.esiEmployer || 0 },
                        { label: "PF Ee", value: pulse.totals.pfEmployee || 0 },
                        { label: "ESI Ee", value: pulse.totals.esiEmployee || 0 },
                        { label: "PT", value: pulse.totals.professionalTax || 0 },
                        { label: "TDS", value: pulse.totals.tds || 0 },
                      ]}
                      size={110}
                    />
                    <div className="mt-2 text-[10px] text-[hsl(var(--ds-muted-fg))]">
                      Coverage · PF {analytics?.statutory?.pfApplicable ?? "—"} · ESI{" "}
                      {analytics?.statutory?.esiApplicable ?? "—"} · EPS{" "}
                      {analytics?.statutory?.epsApplicable ?? "—"}
                    </div>
                    </DsCard>
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <DsCard padding="sm" className="!p-3">
                    <div className="mb-2 ds-small uppercase text-[hsl(var(--ds-muted-fg))]">
                      Master-wise cost (top)
                    </div>
                    <DashHBar data={masterChartData} />
                  </DsCard>
                  <DsCard padding="sm" className="!p-3">
                    <div className="mb-2 ds-small uppercase text-[hsl(var(--ds-muted-fg))]">
                      Headcount trend
                    </div>
                    <DashLine
                      labels={seriesLabels}
                      values={seriesHc}
                      color={CHART_HEX[5]}
                      valueFormat="count"
                    />
                  </DsCard>
                </div>

                <div className="mt-4">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      {compareMonths.length ? "Comparison" : "Trend / series"}
                    </div>
                    {compareMonths.length ? (
                      <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
                        Aspects:{" "}
                        {COMPARE_ASPECTS.filter((a) => compareAspects.includes(a.id))
                          .map((a) => a.label)
                          .join(", ")}
                      </div>
                    ) : null}
                  </div>
                  <DsCard padding="sm" className="!p-0 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="ds-table w-full text-xs">
                        <thead>
                          <tr>
                            <th className="px-3 py-2 text-left">Period</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            {compareAspects.includes("headcount") ? (
                              <th className="px-3 py-2 text-right">HC</th>
                            ) : null}
                            {compareAspects.includes("fixedCost") ? (
                              <th className="px-3 py-2 text-right">Fixed</th>
                            ) : null}
                            {compareAspects.includes("totalCost") ? (
                              <th className="px-3 py-2 text-right">Cost</th>
                            ) : null}
                            {compareAspects.includes("cash") ? (
                              <th className="px-3 py-2 text-right">Cash</th>
                            ) : null}
                            {compareAspects.includes("net") ? (
                              <th className="px-3 py-2 text-right">Net</th>
                            ) : null}
                            {compareAspects.includes("tds") ||
                            compareAspects.includes("taxRegime") ? (
                              <th className="px-3 py-2 text-right">TDS</th>
                            ) : null}
                            {compareAspects.includes("taxRegime") ? (
                              <th className="px-3 py-2 text-right">Tax O/N</th>
                            ) : null}
                            {compareAspects.includes("revisions") ? (
                              <th className="px-3 py-2 text-right">Revisions</th>
                            ) : null}
                            {compareAspects.includes("fnf") ? (
                              <th className="px-3 py-2 text-right">F&amp;F</th>
                            ) : null}
                            {compareAspects.includes("attendance") ? (
                              <th className="px-3 py-2 text-right">Att open</th>
                            ) : null}
                          </tr>
                        </thead>
                        <tbody>
                          {compareSeries.length === 0 ? (
                            <tr>
                              <td
                                colSpan={12}
                                className="px-3 py-6 text-center text-[hsl(var(--ds-muted-fg))]"
                              >
                                No series data for this period.
                              </td>
                            </tr>
                          ) : (
                            compareSeries.map((row) => (
                              <tr key={row.label} className="border-t border-[hsl(var(--ds-border))]">
                                <td className="px-3 py-1.5 font-medium">{row.label}</td>
                                <td className="px-3 py-1.5">{statusBadgeLabel(row.status)}</td>
                                {compareAspects.includes("headcount") ? (
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {row.headcount}
                                  </td>
                                ) : null}
                                {compareAspects.includes("fixedCost") ? (
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatInrLakhs(row.fixedCost)}
                                  </td>
                                ) : null}
                                {compareAspects.includes("totalCost") ? (
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatInrLakhs(row.totalCost)}
                                  </td>
                                ) : null}
                                {compareAspects.includes("cash") ? (
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatInrLakhs(row.cash)}
                                  </td>
                                ) : null}
                                {compareAspects.includes("net") ? (
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatInrLakhs(row.net)}
                                  </td>
                                ) : null}
                                {compareAspects.includes("tds") ||
                                compareAspects.includes("taxRegime") ? (
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatInrLakhs(row.tds || 0)}
                                  </td>
                                ) : null}
                                {compareAspects.includes("taxRegime") ? (
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {row.taxOld ?? "—"}/{row.taxNew ?? "—"}
                                  </td>
                                ) : null}
                                {compareAspects.includes("revisions") ? (
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {row.revisionsCount ?? 0}
                                  </td>
                                ) : null}
                                {compareAspects.includes("fnf") ? (
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {row.fnfCount ?? 0}
                                  </td>
                                ) : null}
                                {compareAspects.includes("attendance") ? (
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {row.attOpen ?? 0}
                                  </td>
                                ) : null}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {compareSeries.length > 0 &&
                    (compareAspects.includes("totalCost") ||
                      compareAspects.includes("cash")) ? (
                      <div className="border-t border-[hsl(var(--ds-border))] px-3 py-3">
                        <DashCombo
                          labels={compareSeries.map((r) => r.label)}
                          barValues={compareSeries.map((r) => r.totalCost)}
                          lineValues={compareSeries.map((r) => r.cash)}
                          barLabel="Cost"
                          lineLabel="Cash"
                        />
                      </div>
                    ) : null}
                    {compareMonths.length > 0 &&
                    (compareAspects.includes("fnf") ||
                      compareAspects.includes("revisions") ||
                      compareAspects.includes("attendance") ||
                      compareAspects.includes("taxRegime") ||
                      compareAspects.includes("statutory") ||
                      compareAspects.includes("masters")) ? (
                      <div className="border-t border-[hsl(var(--ds-border))] px-3 py-3 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                        Extra aspects (tax, statutory, F&amp;F, revisions, attendance,
                        masters) update on their tabs for these compare months. Excel/PDF
                        include the full comparison summary.
                      </div>
                    ) : null}
                  </DsCard>
                </div>

                <div className="mt-4">
                  <div className="mb-2 flex items-center gap-2">
                    <DsIcon icon={Building2} size="sm" className="opacity-70" />
                    <div className="ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Master-wise · {groupBy}
                      {masterComparePivot ? " (compare by month)" : ""}
                    </div>
                  </div>
                  {masterComparePivot ? (
                    <DsCard padding="sm" className="!p-0 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="ds-table w-full text-xs">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left sticky left-0 bg-[hsl(var(--ds-surface))]">
                                Master
                              </th>
                              {masterComparePivot.months.map((ym) => (
                                <th
                                  key={ym}
                                  className="px-3 py-2 text-right"
                                  colSpan={2}
                                >
                                  {ym}
                                </th>
                              ))}
                            </tr>
                            <tr>
                              <th className="px-3 py-1 text-left sticky left-0 bg-[hsl(var(--ds-surface))]" />
                              {masterComparePivot.months.map((ym) => (
                                <React.Fragment key={`${ym}-sub`}>
                                  <th className="px-2 py-1 text-right text-[10px] font-normal">
                                    HC
                                  </th>
                                  <th className="px-2 py-1 text-right text-[10px] font-normal">
                                    Earned
                                  </th>
                                </React.Fragment>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {masterComparePivot.rows.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={1 + masterComparePivot.months.length * 2}
                                  className="px-3 py-6 text-center text-[hsl(var(--ds-muted-fg))]"
                                >
                                  No master split for compare months.
                                </td>
                              </tr>
                            ) : (
                              masterComparePivot.rows.map((row) => (
                                <tr
                                  key={row.label}
                                  className="border-t border-[hsl(var(--ds-border))]"
                                >
                                  <td className="px-3 py-1.5 font-medium sticky left-0 bg-[hsl(var(--ds-surface))]">
                                    {row.label}
                                  </td>
                                  {masterComparePivot.months.map((ym) => {
                                    const cell = row.byMonth[ym] || {};
                                    return (
                                      <React.Fragment key={`${row.label}-${ym}`}>
                                        <td className="px-2 py-1.5 text-right tabular-nums">
                                          {cell.headcount ?? "—"}
                                        </td>
                                        <td className="px-2 py-1.5 text-right tabular-nums">
                                          {cell.earnedCost != null
                                            ? formatInrLakhs(cell.earnedCost)
                                            : "—"}
                                        </td>
                                      </React.Fragment>
                                    );
                                  })}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </DsCard>
                  ) : (
                    <DsCard padding="sm" className="!p-0 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="ds-table w-full text-xs">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left">Master</th>
                              <th className="px-3 py-2 text-right">HC</th>
                              <th className="px-3 py-2 text-right">Fixed</th>
                              <th className="px-3 py-2 text-right">Earned</th>
                              <th className="px-3 py-2 text-right">Cash out</th>
                              <th className="px-3 py-2 text-right">TDS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(analytics?.masters || []).length === 0 ? (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="px-3 py-6 text-center text-[hsl(var(--ds-muted-fg))]"
                                >
                                  Master split available after payroll is processed.
                                </td>
                              </tr>
                            ) : (
                              (analytics.masters || []).slice(0, 20).map((row) => (
                                <tr
                                  key={row.label}
                                  className="border-t border-[hsl(var(--ds-border))]"
                                >
                                  <td className="px-3 py-1.5 font-medium">{row.label}</td>
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {row.headcount}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatInrLakhs(row.fixedCost)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatInrLakhs(row.earnedCost)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatInrLakhs(row.cashOutflow)}
                                  </td>
                                  <td className="px-3 py-1.5 text-right tabular-nums">
                                    {formatInrLakhs(row.tds)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </DsCard>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <CompareFormModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        availableMonths={availableForCompare}
        selected={compareMonths}
        selectedAspects={compareAspects}
        onApply={({ months, aspects }) => {
          setCompareMonths(months || []);
          setCompareAspects(
            aspects?.length ? aspects : COMPARE_ASPECTS.map((a) => a.id)
          );
        }}
      />
      <CardDetailModal
        open={!!cardDetail}
        detail={cardDetail}
        onClose={() => setCardDetail(null)}
        canExport={canExport}
      />
    </>
  );
}
